import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import api from "@/lib/api";
import { useShop } from "@/lib/useShop";
import TypeChips from "@/components/simple/product/TypeChips";
import ShirtBody from "@/components/simple/product/ShirtBody";
import SignBody from "@/components/simple/product/SignBody";
import SheetBody from "@/components/simple/product/SheetBody";
import ArtworkBody from "@/components/simple/product/ArtworkBody";
import { NotFound } from "@/components/simple/product/Fields";

/** Which card (or product) a Simple route is asking for (2.4: every old route lands on the one product page). */
function keyFor(pathname, params, fixed) {
    if (fixed) return fixed;
    if (params.slug) return params.slug;
    if (pathname === "/shirts/dtf/gang-sheet") return "gangsheet";
    if (pathname === "/shirts/dtf") return "transfers";
    if (pathname.startsWith("/shirts")) return params.kind || "shirts";
    if (pathname.startsWith("/signs")) return params.category || "signs";
    if (pathname === "/artwork-request") return "artwork";
    return "shirts";
}

/** The one generic product page (5.1): type chips · photo/design · size & quantity · note · ready-by · price · Pay now. */
export default function SimpleProduct({ cardKey }) {
    const params = useParams();
    const { pathname } = useLocation();
    const [sp] = useSearchParams();
    const { shopSlug } = useShop();
    const key = keyFor(pathname, params, cardKey);
    const [data, setData] = useState(null);
    const [missing, setMissing] = useState(false);
    const [chipId, setChipId] = useState(null);

    useEffect(() => {
        setData(null); setMissing(false); setChipId(null);
        api.get(`/simple/product/${encodeURIComponent(key)}`, { params: shopSlug ? { store: shopSlug } : {} })
            .then(({ data }) => setData(data)).catch(() => setMissing(true));
    }, [key, shopSlug]);

    const productsBySlug = useMemo(() => Object.fromEntries((data?.products || []).map((p) => [p.slug, p])), [data]);
    const wanted = sp.get("type") || data?.type;
    const chip = useMemo(() => {
        const chips = data?.chips || [];
        if (!chips.length) return null;
        if (chipId) return chips.find((c) => c.id === chipId) || chips[0];
        return chips.find((c) => c.id === wanted || c.products.some((p) => p.slug === wanted)) || chips[0];
    }, [data, chipId, wanted]);

    const back = data?.store?.slug && data.store.slug !== "13th-ave-sign-and-shirt" ? `/stores/${data.store.slug}` : "/";
    const title = data?.title || "Order";
    // Phase 9: the Flyers & Print card has its own template page; the old /order/flyers address is an alias (11.2 rule 11)
    if (data?.designer === "print") return <Navigate to={`/print/${data.key}${shopSlug ? `?store=${encodeURIComponent(shopSlug)}` : ""}`} replace />;

    const body = () => {
        if (!data) return null;
        const common = { store: data.store, cleanupFee: data.cleanup_fee, placeholder: data.note_placeholder, ready: data.ready };
        if (data.designer === "upload") return <ArtworkBody {...common} image="/assets/art/ballers.webp" />;
        if (data.designer === "gangsheet" || data.designer === "dtf_pieces") return <SheetBody key={data.key} mode={data.key} dtf={data.dtf} {...common} />;
        if (!chip) return null;
        if (chip.designer === "print") return <Navigate to={`/print/${chip.print_group}${shopSlug ? `?store=${encodeURIComponent(shopSlug)}` : ""}`} replace />;
        if (data.designer === "shirt") return <ShirtBody chip={chip} productsBySlug={productsBySlug} allowSupplied={data.customer_supplied_allowed} {...common} />;
        const product = productsBySlug[chip.products[0]?.slug];
        return product ? <SignBody key={product.slug} product={product} sizeMode={data.size_mode} {...common} /> : null;
    };

    return (
        <Layout title={title}>
            <article data-testid="simple-product" data-card={data?.key} className="mx-auto w-full max-w-4xl">
                <div className="px-3 pt-4 sm:px-6">
                    <Link to={back} className="inline-flex min-h-[44px] items-center text-[16px] underline" data-testid="product-back">← Back</Link>
                    <h1 className="mt-1 text-[28px] font-semibold leading-tight" data-testid="product-title">{title}</h1>
                </div>
                {missing && <NotFound store={data?.store} />}
                {!missing && !data && <p className="px-3 py-6 text-muted-ink sm:px-6" data-testid="product-loading">Loading…</p>}
                {data && (
                    <div className="px-3 pt-3 sm:px-6">
                        <TypeChips chips={data.chips} value={chip?.id} onChange={setChipId} />
                    </div>
                )}
                {body()}
            </article>
        </Layout>
    );
}
